import { createSafeJotaiLocalStorage, getSafeLocalStorage } from '@/lib/jotai-storage';
import { atom, getDefaultStore, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback, useLayoutEffect, useRef } from 'react';

import { WORK_CHAT_HOME_PATH } from '@heidi/agents/contracts';

import { trackNavV3ButtonClicked } from './nav-v3-analytics';

// Browser-storage key for the persisted collapse preference, not a TanStack cache
// identity — declared beside its atom so persistence-manifest resolves the literal.
export const AGENT_HOME_PANEL_COLLAPSED_KEY = 'heidi-agent-home-panel-collapsed';

/** Persisted when the user explicitly toggles the panel closed or open. */
export const agentHomePanelCollapsedPreferenceAtom = atomWithStorage<boolean>(
  AGENT_HOME_PANEL_COLLAPSED_KEY,
  false,
  createSafeJotaiLocalStorage<boolean>(),
  { getOnInit: true }
);

/** Live open state; restored from the persisted preference on mount. */
export const agentHomePanelOpenAtom = atom(true);

/**
 * The Work route publishes this: true only on the Home landing (no project,
 * not Routines / Discover / a guided create). Those drill-ins share
 * `/scribe/chat/projects`, so the URL alone cannot tell them apart.
 */
export const isAgentHomeLandingAtom = atom(false);

/**
 * Exact Home landing path (query/hash ignored). Not the primary-rail highlight —
 * use `isAgentHomeRailActive` for that.
 */
export const isAgentHomeActive = (asPath: string, baseChatHref: string): boolean =>
  asPath.replace(/[?#].*$/, '') === baseChatHref;

/** A routed Work project chat (`/scribe/chat/projects/:id`). */
export const isAgentHomeProjectChat = (asPath: string, baseChatHref: string): boolean => {
  const path = asPath.replace(/[?#].*$/, '');
  return (
    baseChatHref === WORK_CHAT_HOME_PATH && path.startsWith(`${WORK_CHAT_HOME_PATH}/`)
  );
};

/** Primary-rail Home highlight: Work landing and its project chats. */
export const isAgentHomeRailActive = (asPath: string, baseChatHref: string): boolean =>
  isAgentHomeActive(asPath, baseChatHref) || isAgentHomeProjectChat(asPath, baseChatHref);

export const shouldShowAgentHomePanel = ({
  asPath,
  baseChatHref,
  isOpen,
  isMobile,
  isHomeLanding,
  isProjectBound,
  isRoutinesView,
}: {
  asPath: string;
  baseChatHref: string;
  isOpen: boolean;
  isMobile: boolean;
  isHomeLanding: boolean;
  isProjectBound: boolean;
  isRoutinesView: boolean;
}): boolean => {
  const onWorkHomeTree =
    isAgentHomeActive(asPath, baseChatHref) ||
    isAgentHomeProjectChat(asPath, baseChatHref);

  return (
    !isMobile &&
    isOpen &&
    onWorkHomeTree &&
    (isAgentHomeProjectChat(asPath, baseChatHref) ||
      isHomeLanding ||
      isProjectBound ||
      isRoutinesView)
  );
};

export const syncAgentHomePanelFromPreference = (): void => {
  const store = getDefaultStore();
  const collapsed = store.get(agentHomePanelCollapsedPreferenceAtom);
  store.set(agentHomePanelOpenAtom, !collapsed);
};

/** Restores the persisted preference once when Agent Home mounts. */
export function useAgentHomePanelPreferenceSync(enabled: boolean): void {
  const hasSyncedOnMountRef = useRef(false);
  useLayoutEffect(() => {
    if (!enabled || hasSyncedOnMountRef.current) return;
    hasSyncedOnMountRef.current = true;
    syncAgentHomePanelFromPreference();
  }, [enabled]);
}

export function useAgentHomePanel() {
  const [isOpen, setIsOpen] = useAtom(agentHomePanelOpenAtom);
  const [, setCollapsedPreference] = useAtom(agentHomePanelCollapsedPreferenceAtom);

  const open = useCallback(() => {
    setIsOpen(true);
    setCollapsedPreference(false);
  }, [setCollapsedPreference, setIsOpen]);

  const toggle = useCallback(() => {
    const next = !isOpen;
    trackNavV3ButtonClicked({ label: 'agent_home_panel_toggle', is_enabled: next });
    setIsOpen(next);
    setCollapsedPreference(!next);
  }, [isOpen, setCollapsedPreference, setIsOpen]);

  return { isOpen, open, toggle };
}

export const clearAgentHomePanelPreference = (): void => {
  const store = getDefaultStore();
  store.set(agentHomePanelCollapsedPreferenceAtom, false);
  store.set(agentHomePanelOpenAtom, true);
  getSafeLocalStorage().removeItem(AGENT_HOME_PANEL_COLLAPSED_KEY);
};
